import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, ViewStyle, TextStyle } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'outline';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

/**
 * Reusable button component with marketplace design system variants
 * 
 * Variants:
 * - primary: Blue filled button (default action)
 * - secondary: Blue outline button
 * - danger: Red filled button (destructive action)
 * - outline: Transparent with border (cancel action)
 */
export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  size = 'medium',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  style,
  textStyle,
}) => {
  const isDisabled = disabled || isLoading;

  const getContainerStyle = (): ViewStyle[] => {
    const baseStyle: ViewStyle[] = [styles.container, sizeStyles[size].container];
    
    if (fullWidth) {
      baseStyle.push(styles.fullWidth);
    }

    switch (variant) {
      case 'primary':
        baseStyle.push(styles.primaryContainer);
        if (isDisabled) baseStyle.push(styles.primaryDisabled);
        break;
      case 'secondary':
        baseStyle.push(styles.secondaryContainer);
        if (isDisabled) baseStyle.push(styles.secondaryDisabled);
        break;
      case 'danger':
        baseStyle.push(styles.dangerContainer);
        if (isDisabled) baseStyle.push(styles.dangerDisabled);
        break;
      case 'outline':
        baseStyle.push(styles.outlineContainer);
        if (isDisabled) baseStyle.push(styles.outlineDisabled);
        break;
    }

    if (style) baseStyle.push(style);
    return baseStyle;
  };

  const getTextStyle = (): TextStyle[] => {
    const baseStyle: TextStyle[] = [styles.text, sizeStyles[size].text];

    switch (variant) {
      case 'primary':
        baseStyle.push(styles.primaryText);
        if (isDisabled) baseStyle.push(styles.primaryTextDisabled);
        break;
      case 'secondary':
        baseStyle.push(styles.secondaryText);
        if (isDisabled) baseStyle.push(styles.secondaryTextDisabled);
        break;
      case 'danger':
        baseStyle.push(styles.dangerText);
        if (isDisabled) baseStyle.push(styles.dangerTextDisabled);
        break;
      case 'outline':
        baseStyle.push(styles.outlineText);
        if (isDisabled) baseStyle.push(styles.outlineTextDisabled);
        break;
    }

    if (textStyle) baseStyle.push(textStyle);
    return baseStyle;
  };

  const getLoadingColor = (): string => {
    switch (variant) {
      case 'primary':
        return MARKETPLACE_COLORS.textPrimary;
      case 'secondary':
        return MARKETPLACE_COLORS.accentBlueSolid;
      case 'danger':
        return MARKETPLACE_COLORS.background;
      case 'outline':
        return MARKETPLACE_COLORS.textPrimary;
      default:
        return MARKETPLACE_COLORS.textPrimary;
    }
  };

  return (
    <TouchableOpacity
      style={getContainerStyle()}
      onPress={onPress}
      activeOpacity={0.8}
      disabled={isDisabled}
    >
      {isLoading ? (
        <ActivityIndicator size="small" color={getLoadingColor()} />
      ) : (
        <Text style={getTextStyle()}>{title}</Text>
      )}
    </TouchableOpacity>
  );
};

// Size configurations
const sizeStyles = {
  small: StyleSheet.create({
    container: {
      height: 32,
      paddingHorizontal: 12,
    },
    text: {
      fontSize: 12,
      lineHeight: 18,
    },
  }),
  medium: StyleSheet.create({
    container: {
      height: 40,
      paddingHorizontal: 16,
    },
    text: {
      fontSize: 14,
      lineHeight: 21,
    },
  }),
  large: StyleSheet.create({
    container: {
      height: 48,
      paddingHorizontal: 24,
    },
    text: {
      fontSize: 16,
      lineHeight: 24,
    },
  }),
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  fullWidth: {
    width: '100%',
  },
  text: {
    fontWeight: '500',
  },

  // Primary variant (blue filled)
  primaryContainer: {
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
  },
  primaryDisabled: {
    backgroundColor: MARKETPLACE_COLORS.buttonDisabled,
  },
  primaryText: {
    color: MARKETPLACE_COLORS.textPrimary,
  },
  primaryTextDisabled: {
    color: MARKETPLACE_COLORS.buttonDisabledText,
  },

  // Secondary variant (blue outline)
  secondaryContainer: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.accentBlueSolid,
  },
  secondaryDisabled: {
    borderColor: MARKETPLACE_COLORS.buttonDisabled,
  },
  secondaryText: {
    color: MARKETPLACE_COLORS.accentBlue,
  },
  secondaryTextDisabled: {
    color: MARKETPLACE_COLORS.textMuted,
  },

  // Danger variant (red filled)
  dangerContainer: {
    backgroundColor: MARKETPLACE_COLORS.accentRed,
  },
  dangerDisabled: {
    backgroundColor: MARKETPLACE_COLORS.buttonDisabled,
  },
  dangerText: {
    color: MARKETPLACE_COLORS.background,
  },
  dangerTextDisabled: {
    color: MARKETPLACE_COLORS.buttonDisabledText,
  },

  // Outline variant (transparent with border)
  outlineContainer: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.textPrimary,
  },
  outlineDisabled: {
    borderColor: MARKETPLACE_COLORS.buttonDisabled,
  },
  outlineText: {
    color: MARKETPLACE_COLORS.textPrimary,
  },
  outlineTextDisabled: {
    color: MARKETPLACE_COLORS.textMuted,
  },
});
